/**
 * ConvertToProjectModal
 *
 * Modal for converting selected investment items to a renovation project.
 * Features:
 * - Clear sections for Project and Projectonderdelen
 * - Add new project parts by picking label + unit
 * - Either pick from matching investment lines or create new budget
 * - Custom naming for each project part
 * - Partial budget allocation support
 */

import { useState, useEffect, useMemo, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "components/ui/dialog";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { Textarea } from "components/ui/textarea";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "components/ui/alert-dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "components/ui/tooltip";
import {
  Rocket,
  Spinner,
  Buildings,
  Tag,
  Cube,
  PencilSimple,
  Plus,
  Trash,
  ListBullets,
  CurrencyEur,
  MagnifyingGlass,
  Warning,
  ArrowUp,
  PlusCircle,
  Percent,
  Ruler,
  ArrowLeft,
} from "@phosphor-icons/react";
import { useSession } from "@clerk/clerk-react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { toast } from "react-toastify";
import type {
  InvestmentItem,
  ConvertToProjectDto,
  ItemAllocationDto,
  NewBudgetAllocationDto,
  RenovationLabel,
} from "../types/investment";
import { AuditLogChangeType } from "../types/investment";
import { UnitDTO } from "shared/types/unit";
import { ContactCompany } from "shared/types/contact";
import { SupplierSearchSelect } from "screens/buildings/components/renovationTab/modals/components/SupplierSearchSelect";

interface ConvertToProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  selectedItems: InvestmentItem[];
  buildingId: string;
}

// Format currency helper
const formatCurrency = (amount: number) =>
  new Intl.NumberFormat("nl-BE", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);

// Truncate text to first N sentences
const truncateToSentences = (text: string | undefined, maxSentences: number = 2): string => {
  if (!text) return "";
  // Split by sentence-ending punctuation followed by space or end
  const sentences = text.match(/[^.!?]+[.!?]+/g) || [text];
  if (sentences.length <= maxSentences) return text;
  return sentences.slice(0, maxSentences).join("").trim() + "...";
};

// Get creation reason from audit logs
const getCreationReason = (item: InvestmentItem): string | null => {
  const createdLog = item.auditLogs?.find(
    (log) => log.changeType === AuditLogChangeType.CREATED
  );
  return createdLog?.reason || null;
};

// Part types
type PartSource = "existing" | "new";
type OverflowOption = "increase" | "new_line" | null;

// Unit distribution: { unitId: percentage }
type UnitDistribution = Record<string, number>;

interface PartConfig {
  id: string;
  source: PartSource;
  // For existing investment line allocation
  sourceItemId?: number;
  // For new budget
  labelId?: number;
  labelName?: string;
  // Common fields
  partName: string;
  unitIds: string[]; // Multiple units for pro-rata distribution
  amount: number;
  // Unit distribution percentages (when multiple units)
  unitDistribution?: UnitDistribution;
  // For budget overflow handling
  budgetIncreaseAmount?: number; // Amount to increase the source investment line by
  budgetIncreaseReason?: string; // Reason for the budget increase
  // Optional supplier to link immediately after project creation
  supplier?: ContactCompany;
}

// Add part dialog state - Flow: Unit first → See budgets (with label filter) → Select or create new → Distribution
interface AddPartDialogState {
  isOpen: boolean;
  step: "unit" | "source" | "distribution"; // Unit selection first, then source/budget selection, then distribution
  unitIds: string[]; // Selected units
  labelFilter: number | null; // Optional filter for investment lines by label
  matchingItems: InvestmentItem[]; // Investment lines matching the selected units
  selectedItemId: number | null;
  isNewBudget: boolean;
  newBudgetLabelId: number | null; // Label for new budget
  partName: string;
  amount: number;
  // Overflow handling when amount exceeds available budget
  overflowOption: OverflowOption; // "increase" = increase existing line, "new_line" = create new line for overflow
  overflowReason: string; // Reason for budget increase (required when overflowOption === "increase")
  // Unit distribution percentages
  unitDistribution: UnitDistribution;
}

export const ConvertToProjectModal = ({
  isOpen,
  onClose,
  onSuccess,
  selectedItems,
  buildingId,
}: ConvertToProjectModalProps) => {
  const { session } = useSession();
  const [projectTitle, setProjectTitle] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Part configurations
  const [parts, setParts] = useState<PartConfig[]>([]);

  // All investment items for the building (to search for matching ones)
  const [allInvestmentItems, setAllInvestmentItems] = useState<InvestmentItem[]>([]);

  // Labels for picker
  const [labels, setLabels] = useState<RenovationLabel[]>([]);
  const [loadingLabels, setLoadingLabels] = useState(false);

  // Units for the building
  const [buildingUnits, setBuildingUnits] = useState<UnitDTO[]>([]);
  const [loadingUnits, setLoadingUnits] = useState(false);

  // Add part dialog state
  const [addPartDialog, setAddPartDialog] = useState<AddPartDialogState>({
    isOpen: false,
    step: "unit",
    unitIds: [],
    labelFilter: null,
    matchingItems: [],
    selectedItemId: null,
    isNewBudget: false,
    newBudgetLabelId: null,
    partName: "",
    amount: 0,
    overflowOption: null,
    overflowReason: "",
    unitDistribution: {},
  });

  // Search/filter state for dialogs
  const [unitSearch, setUnitSearch] = useState("");
  const [budgetSearch, setBudgetSearch] = useState("");

  // Generate unique ID
  const generateId = useCallback(() => {
    return `part-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }, []);

  // Fetch labels when modal opens
  useEffect(() => {
    if (isOpen && session) {
      setLoadingLabels(true);
      fetchWithAuth(`${API_URL}/renovation/labels`, { method: "GET" }, session)
        .then((res) => res.json())
        .then((data) => {
          setLabels(Array.isArray(data) ? data : []);
        })
        .catch((err) => console.error("Error loading labels:", err))
        .finally(() => setLoadingLabels(false));
    }
  }, [isOpen, session]);

  // Fetch building units when modal opens
  useEffect(() => {
    if (isOpen && buildingId && session) {
      setLoadingUnits(true);
      fetchWithAuth(
        `${API_URL}/units/building/${buildingId}`,
        { method: "GET" },
        session
      )
        .then((res) => res.json())
        .then((data) => {
          setBuildingUnits(Array.isArray(data) ? data : data.data || []);
        })
        .catch((err) => console.error("Error loading units:", err))
        .finally(() => setLoadingUnits(false));
    }
  }, [isOpen, buildingId, session]);

  // Fetch all investment items for the building
  useEffect(() => {
    if (isOpen && buildingId && session) {
      fetchWithAuth(
        `${API_URL}/investment/items?buildingId=${buildingId}&excludeConverted=true`,
        { method: "GET" },
        session
      )
        .then((res) => res.json())
        .then((data) => {
          const items = data.data || data || [];
          setAllInvestmentItems(items);
        })
        .catch((err) => console.error("Error loading investment items:", err));
    }
  }, [isOpen, buildingId, session]);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setProjectTitle("");
      // Initialize with one part per pre-selected item
      const initialParts: PartConfig[] = selectedItems.map((item) => {
        const allocated = Number(item.allocatedBudget) || 0;
        const total = Number(item.estimatedCostExclVat) || 0;
        const remaining = total - allocated;
        return {
          id: generateId(),
          source: "existing" as PartSource,
          sourceItemId: item.id,
          partName: item.title,
          unitIds: item.units?.map((u) => u.id).filter(Boolean) as string[] || [],
          amount: remaining > 0 ? remaining : 0,
        };
      });
      setParts(initialParts);
    }
  }, [isOpen, selectedItems, generateId]);

  // Get item by ID (from pre-selected or all items)
  const getItemById = useCallback(
    (itemId: number): InvestmentItem | undefined => {
      return (
        selectedItems.find((i) => i.id === itemId) ||
        allInvestmentItems.find((i) => i.id === itemId)
      );
    },
    [selectedItems, allInvestmentItems]
  );

  // Calculate totals
  const calculations = useMemo(() => {
    let totalFromExisting = 0;
    let totalFromNew = 0;

    parts.forEach((part) => {
      if (part.source === "existing") {
        totalFromExisting += part.amount || 0;
      } else {
        totalFromNew += part.amount || 0;
      }
    });

    return {
      totalFromExisting,
      totalFromNew,
      total: totalFromExisting + totalFromNew,
    };
  }, [parts]);

  // Get building info
  const buildingInfo = selectedItems[0]?.building;

  // Update a part
  const updatePart = (partId: string, updates: Partial<PartConfig>) => {
    setParts((prev) =>
      prev.map((p) => (p.id === partId ? { ...p, ...updates } : p))
    );
  };

  // Remove a part
  const removePart = (partId: string) => {
    setParts((prev) => prev.filter((p) => p.id !== partId));
  };

  // Handle amount change with validation
  const handleAmountChange = (partId: string, value: string) => {
    const numValue = value === "" ? 0 : parseFloat(value);
    const validValue = isNaN(numValue) ? 0 : Math.max(0, numValue);
    updatePart(partId, { amount: validValue });
  };

  // Check if a part has overflow (amount exceeds remaining budget)
  const getPartOverflow = (part: PartConfig): { hasOverflow: boolean; overflowAmount: number; remaining: number } => {
    if (part.source !== "existing" || !part.sourceItemId) {
      return { hasOverflow: false, overflowAmount: 0, remaining: 0 };
    }
    const item = getItemById(part.sourceItemId);
    if (!item) {
      return { hasOverflow: false, overflowAmount: 0, remaining: 0 };
    }
    const allocated = Number(item.allocatedBudget) || 0;
    const total = Number(item.estimatedCostExclVat) || 0;
    const remaining = total - allocated;
    const overflowAmount = part.amount - remaining;
    return {
      hasOverflow: overflowAmount > 0,
      overflowAmount: Math.max(0, overflowAmount),
      remaining,
    };
  };

  // Filtered units based on search
  const filteredUnits = useMemo(() => {
    if (!unitSearch.trim()) return buildingUnits.filter((u) => u.id);
    const search = unitSearch.toLowerCase().trim();
    return buildingUnits.filter(
      (unit) => unit.id && unit.id.toLowerCase().includes(search)
    );
  }, [buildingUnits, unitSearch]);

  // Get matching investment items for selected units (with optional label filter)
  const getMatchingItemsForUnits = useCallback(
    (unitIds: string[], labelFilterId: number | null) => {
      return allInvestmentItems.filter((item) => {
        // Check at least one selected unit is in item's units
        if (!item.units?.some((u) => unitIds.includes(u.id!))) return false;
        // Check label filter if set
        if (labelFilterId !== null && item.label?.id !== labelFilterId) return false;
        // Check has remaining budget
        const allocated = Number(item.allocatedBudget) || 0;
        const total = Number(item.estimatedCostExclVat) || 0;
        return total - allocated > 0;
      });
    },
    [allInvestmentItems]
  );

  // Filtered matching items based on search
  const filteredMatchingItems = useMemo(() => {
    if (!budgetSearch.trim()) return addPartDialog.matchingItems;
    const search = budgetSearch.toLowerCase().trim();
    return addPartDialog.matchingItems.filter(
      (item) =>
        item.title.toLowerCase().includes(search) ||
        item.label?.name?.toLowerCase().includes(search)
    );
  }, [addPartDialog.matchingItems, budgetSearch]);

  // Open add part dialog
  const openAddPartDialog = () => {
    setUnitSearch("");
    setBudgetSearch("");
    setAddPartDialog({
      isOpen: true,
      step: "unit",
      unitIds: [],
      labelFilter: null,
      matchingItems: [],
      selectedItemId: null,
      isNewBudget: false,
      newBudgetLabelId: null,
      partName: "",
      amount: 0,
      overflowOption: null,
      overflowReason: "",
      unitDistribution: {},
    });
  };

  // Get unit details including surface
  const getUnitDetails = useCallback(
    (unitId: string) => {
      return buildingUnits.find((u) => u.id === unitId);
    },
    [buildingUnits]
  );

  // Initialize equal distribution
  const initializeEqualDistribution = useCallback((unitIds: string[]) => {
    const equalPercentage = 100 / unitIds.length;
    const distribution: UnitDistribution = {};
    unitIds.forEach((unitId) => {
      distribution[unitId] = Math.round(equalPercentage * 100) / 100;
    });
    return distribution;
  }, []);

  // Handle proceed to distribution step
  const handleProceedToDistribution = () => {
    const initialDistribution = initializeEqualDistribution(addPartDialog.unitIds);
    setAddPartDialog((prev) => ({
      ...prev,
      step: "distribution",
      unitDistribution: initialDistribution,
    }));
  };

  // Handle distribution percentage change
  const handleDistributionChange = (unitId: string, value: number) => {
    setAddPartDialog((prev) => ({
      ...prev,
      unitDistribution: {
        ...prev.unitDistribution,
        [unitId]: Math.max(0, Math.min(100, value)),
      },
    }));
  };

  // Calculate total distribution percentage
  const totalDistributionPercentage = useMemo(() => {
    return Object.values(addPartDialog.unitDistribution).reduce(
      (sum, pct) => sum + (pct || 0),
      0
    );
  }, [addPartDialog.unitDistribution]);

  // Check if distribution is valid (sums to 100)
  const isDistributionValid = useMemo(() => {
    return Math.abs(totalDistributionPercentage - 100) < 0.01;
  }, [totalDistributionPercentage]);

  // Handle unit selection confirm - proceed to source step
  const handleUnitsConfirm = () => {
    if (addPartDialog.unitIds.length === 0) return;

    // Find all matching investment items for these units (no label filter initially)
    const matching = getMatchingItemsForUnits(addPartDialog.unitIds, null);

    setBudgetSearch("");
    setAddPartDialog((prev) => ({
      ...prev,
      matchingItems: matching,
      labelFilter: null,
      step: "source",
    }));
  };

  // Handle label filter change in source step
  const handleLabelFilterChange = (labelId: number | null) => {
    const matching = getMatchingItemsForUnits(addPartDialog.unitIds, labelId);
    setAddPartDialog((prev) => ({
      ...prev,
      labelFilter: labelId,
      matchingItems: matching,
      selectedItemId: null, // Reset selection when filter changes
      isNewBudget: false,
    }));
  };

  // Handle source selection (existing item or new budget)
  const handleSourceSelect = (itemId: number | null, isNew: boolean) => {
    if (isNew) {
      const unitLabel = addPartDialog.unitIds.length === 1
        ? addPartDialog.unitIds[0]
        : `${addPartDialog.unitIds.length} units`;
      // Use label filter as default if set, otherwise user will select
      const labelFilterId = addPartDialog.labelFilter;
      const labelName = labelFilterId
        ? labels.find(l => l.id === labelFilterId)?.name || "Nieuw budget"
        : "Nieuw budget";
      setAddPartDialog((prev) => ({
        ...prev,
        selectedItemId: null,
        isNewBudget: true,
        newBudgetLabelId: labelFilterId, // Pre-select current filter if set
        partName: `${labelName} - ${unitLabel}`,
        amount: 0,
        overflowOption: null,
        overflowReason: "",
      }));
    } else if (itemId) {
      const item = getItemById(itemId);
      if (item) {
        const allocated = Number(item.allocatedBudget) || 0;
        const total = Number(item.estimatedCostExclVat) || 0;
        const remaining = total - allocated;
        setAddPartDialog((prev) => ({
          ...prev,
          selectedItemId: itemId,
          isNewBudget: false,
          newBudgetLabelId: null,
          partName: item.title,
          amount: remaining,
          overflowOption: null,
          overflowReason: "",
        }));
      }
    }
  };

  // Confirm add part
  const confirmAddPart = () => {
    if (!addPartDialog.partName || addPartDialog.amount <= 0) {
      toast.error("Vul een naam en bedrag in");
      return;
    }

    // Validate distribution if multiple units
    if (addPartDialog.unitIds.length > 1 && !isDistributionValid) {
      toast.error("Verdeling moet optellen tot 100%");
      return;
    }

    // Get distribution (either from dialog or null for single unit)
    const distribution = addPartDialog.unitIds.length > 1
      ? addPartDialog.unitDistribution
      : undefined;

    if (addPartDialog.isNewBudget) {
      // Validate label is selected for new budget
      if (!addPartDialog.newBudgetLabelId) {
        toast.error("Selecteer een categorie voor het nieuwe budget");
        return;
      }
      // Add new budget part
      const label = labels.find(l => l.id === addPartDialog.newBudgetLabelId);
      const newPart: PartConfig = {
        id: generateId(),
        source: "new",
        labelId: addPartDialog.newBudgetLabelId,
        labelName: label?.name || "",
        partName: addPartDialog.partName,
        unitIds: addPartDialog.unitIds,
        amount: addPartDialog.amount,
        unitDistribution: distribution,
      };
      setParts((prev) => [...prev, newPart]);
    } else if (addPartDialog.selectedItemId) {
      // Add part from existing item
      const item = getItemById(addPartDialog.selectedItemId);
      if (!item) return;

      const allocated = Number(item.allocatedBudget) || 0;
      const total = Number(item.estimatedCostExclVat) || 0;
      const remaining = total - allocated;
      const overflowAmount = addPartDialog.amount - remaining;
      const hasOverflow = overflowAmount > 0;

      // Validate overflow handling
      if (hasOverflow && !addPartDialog.overflowOption) {
        toast.error("Kies hoe het extra budget moet worden behandeld");
        return;
      }

      if (hasOverflow && addPartDialog.overflowOption === "increase") {
        // Validate reason is provided for budget increase
        if (!addPartDialog.overflowReason.trim() || addPartDialog.overflowReason.trim().length < 5) {
          toast.error("Geef een reden voor de budgetverhoging (minimaal 5 karakters)");
          return;
        }

        // Add part with budget increase request
        const newPart: PartConfig = {
          id: generateId(),
          source: "existing",
          sourceItemId: addPartDialog.selectedItemId,
          partName: addPartDialog.partName,
          unitIds: addPartDialog.unitIds,
          amount: addPartDialog.amount,
          unitDistribution: distribution,
          budgetIncreaseAmount: overflowAmount,
          budgetIncreaseReason: addPartDialog.overflowReason.trim(),
        };
        setParts((prev) => [...prev, newPart]);
      } else if (hasOverflow && addPartDialog.overflowOption === "new_line") {
        // Validate label is selected for new line
        if (!addPartDialog.newBudgetLabelId) {
          toast.error("Selecteer een categorie voor de nieuwe investeringslijn");
          return;
        }

        // Add part from existing (up to remaining amount)
        const existingPart: PartConfig = {
          id: generateId(),
          source: "existing",
          sourceItemId: addPartDialog.selectedItemId,
          partName: addPartDialog.partName,
          unitIds: addPartDialog.unitIds,
          amount: remaining,
          unitDistribution: distribution,
        };

        // Add new budget part for overflow
        const label = labels.find(l => l.id === addPartDialog.newBudgetLabelId);
        const overflowPart: PartConfig = {
          id: generateId(),
          source: "new",
          labelId: addPartDialog.newBudgetLabelId,
          labelName: label?.name || "",
          partName: `${addPartDialog.partName} (extra)`,
          unitIds: addPartDialog.unitIds,
          amount: overflowAmount,
          unitDistribution: distribution,
        };

        setParts((prev) => [...prev, existingPart, overflowPart]);
      } else {
        // No overflow - add part normally
        const newPart: PartConfig = {
          id: generateId(),
          source: "existing",
          sourceItemId: addPartDialog.selectedItemId,
          partName: addPartDialog.partName,
          unitIds: addPartDialog.unitIds,
          amount: addPartDialog.amount,
          unitDistribution: distribution,
        };
        setParts((prev) => [...prev, newPart]);
      }
    }

    // Close dialog
    setAddPartDialog((prev) => ({ ...prev, isOpen: false }));
  };

  // Submit handler
  const handleSubmit = async () => {
    if (!projectTitle.trim()) {
      toast.error("Vul een projecttitel in");
      return;
    }

    if (parts.length === 0) {
      toast.error("Voeg minstens één projectonderdeel toe");
      return;
    }

    const partsWithAmount = parts.filter((p) => p.amount > 0);
    if (partsWithAmount.length === 0) {
      toast.error("Selecteer minstens één onderdeel met een bedrag");
      return;
    }

    // Validate part names
    for (const part of partsWithAmount) {
      if (!part.partName?.trim()) {
        toast.error("Vul een naam in voor alle projectonderdelen");
        return;
      }
    }

    // Validate overflow handling for parts that exceed available budget
    for (const part of partsWithAmount) {
      const overflow = getPartOverflow(part);
      if (overflow.hasOverflow) {
        // Must have budget increase option selected
        if (!part.budgetIncreaseAmount || part.budgetIncreaseAmount <= 0) {
          toast.error(`Selecteer een optie voor het extra budget bij "${part.partName}"`);
          return;
        }
        // Must have reason for budget increase
        if (!part.budgetIncreaseReason?.trim() || part.budgetIncreaseReason.trim().length < 5) {
          toast.error(`Vul een reden in voor de budgetverhoging bij "${part.partName}" (minimaal 5 karakters)`);
          return;
        }
      }
    }

    setIsSubmitting(true);

    try {
      // Build allocations from existing items
      const allocations: ItemAllocationDto[] = partsWithAmount
        .filter((p) => p.source === "existing" && p.sourceItemId)
        .map((p) => ({
          itemId: p.sourceItemId!,
          amount: p.amount,
          partName: p.partName.trim(),
          unitIds: p.unitIds.length > 0 ? p.unitIds : undefined,
          // Include unit distribution if multiple units
          unitDistribution: p.unitIds.length > 1 && p.unitDistribution
            ? p.unitDistribution
            : undefined,
          budgetIncreaseAmount: p.budgetIncreaseAmount,
          budgetIncreaseReason: p.budgetIncreaseReason,
        }));

      // Build new budget allocations
      const newBudgetAllocations: NewBudgetAllocationDto[] = partsWithAmount
        .filter((p) => p.source === "new" && p.labelId && p.unitIds.length > 0)
        .map((p) => ({
          partName: p.partName.trim(),
          labelId: p.labelId!,
          unitIds: p.unitIds,
          // Include unit distribution if multiple units
          unitDistribution: p.unitIds.length > 1 && p.unitDistribution
            ? p.unitDistribution
            : undefined,
          amount: p.amount,
        }));

      const dto: ConvertToProjectDto = {
        allocations,
        newBudgetAllocations:
          newBudgetAllocations.length > 0 ? newBudgetAllocations : undefined,
        projectTitle: projectTitle.trim(),
        buildingId,
      };

      const response = await fetchWithAuth(
        `${API_URL}/investment/items/convert-to-project`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(dto),
        },
        session
      );

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.message || "Failed to convert to project");
      }

      const project = await response.json();

      // After project creation, assign suppliers to parts that have them selected
      // Parts are created in order: first from existing allocations, then from new allocations
      const partsInOrder = [
        ...partsWithAmount.filter((p) => p.source === "existing" && p.sourceItemId),
        ...partsWithAmount.filter((p) => p.source === "new" && p.labelId && p.unitIds.length > 0),
      ];

      // Map frontend parts to backend parts by index (they're created in the same order)
      const supplierAssignments = partsInOrder
        .map((frontendPart, index) => ({
          partId: project.parts?.[index]?.id,
          supplierId: frontendPart.supplier?.id,
          partName: frontendPart.partName,
        }))
        .filter((a) => a.partId && a.supplierId);

      // Assign suppliers - first ensure folders are created, then assign
      if (supplierAssignments.length > 0) {
        const supplierCount = supplierAssignments.length;
        toast.info(`SharePoint folders worden aangemaakt en leveranciers worden gekoppeld (${supplierCount})...`);

        try {
          // First, ensure project and part folders are created
          // This is necessary because convertToProject doesn't create SharePoint folders
          const ensureFoldersResponse = await fetchWithAuth(
            `${API_URL}/renovation/project/${project.id}/ensure-folders`,
            { method: "POST" },
            session
          );

          if (!ensureFoldersResponse.ok) {
            const err = await ensureFoldersResponse.json().catch(() => ({}));
            console.error("Failed to ensure folders:", err);
            toast.warning(
              `Project aangemaakt, maar folders konden niet worden aangemaakt. Leveranciers kunnen later worden toegevoegd.`
            );
          } else {
            // Folders are ready, now assign suppliers
            const updatedProject = await ensureFoldersResponse.json();

            // Re-map with updated part IDs (in case they changed)
            const updatedAssignments = supplierAssignments.map((assignment, index) => ({
              ...assignment,
              partId: updatedProject.parts?.[index]?.id || assignment.partId,
            }));

            // Process supplier assignments in parallel
            const supplierResults = await Promise.allSettled(
              updatedAssignments.map(async (assignment) => {
                try {
                  const supplierResponse = await fetchWithAuth(
                    `${API_URL}/renovation/project/part/${assignment.partId}/supplier/${assignment.supplierId}`,
                    { method: "POST" },
                    session
                  );
                  if (!supplierResponse.ok) {
                    const err = await supplierResponse.json().catch(() => ({}));
                    throw new Error(err.message || "Failed to assign supplier");
                  }
                  return { success: true, partName: assignment.partName };
                } catch (error) {
                  console.error(`Failed to assign supplier to part ${assignment.partId}:`, error);
                  return { success: false, partName: assignment.partName, error };
                }
              })
            );

            // Check results
            const successCount = supplierResults.filter(
              (r) => r.status === "fulfilled" && r.value.success
            ).length;
            const failedCount = supplierResults.filter(
              (r) => r.status === "rejected" || (r.status === "fulfilled" && !r.value.success)
            ).length;

            if (failedCount > 0) {
              toast.warning(
                `${successCount} van ${supplierCount} leveranciers gekoppeld. ${failedCount} konden niet worden gekoppeld.`
              );
            } else if (successCount > 0) {
              toast.success(`${successCount} leverancier(s) gekoppeld!`);
            }
          }
        } catch (folderError) {
          console.error("Error ensuring folders or assigning suppliers:", folderError);
          toast.warning(
            `Project aangemaakt, maar er was een probleem met folders/leveranciers. Leveranciers kunnen later worden toegevoegd.`
          );
        }
      }

      toast.success(
        `Renovatieproject RENO-${project.id} aangemaakt met budget ${formatCurrency(calculations.total)}`
      );

      onSuccess();
      onClose();
    } catch (error) {
      console.error("Error converting to project:", error);
      toast.error(
        error instanceof Error
          ? error.message
          : "Fout bij aanmaken van renovatieproject"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setProjectTitle("");
      setParts([]);
      onClose();
    }
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-[750px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Rocket className="h-5 w-5 text-moss-primary" />
              Creëer renovatieproject
            </DialogTitle>
            <DialogDescription>
              Configureer het project en de projectonderdelen.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* ==================== SECTION: PROJECT ==================== */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 border-b pb-2">
                <Rocket className="h-4 w-4 text-moss-primary" />
                <h3 className="font-semibold text-gray-900">Project</h3>
              </div>

              <div className="space-y-2">
                <Label htmlFor="projectTitle">Projecttitel *</Label>
                <Input
                  id="projectTitle"
                  value={projectTitle}
                  onChange={(e) => setProjectTitle(e.target.value)}
                  placeholder="Bijv. Renovatie dak en gevel"
                  disabled={isSubmitting}
                  autoFocus
                />
              </div>

              <div className="flex items-center gap-2 text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">
                <Buildings className="h-4 w-4 text-moss-primary" />
                <span className="font-medium">{buildingId}</span>
                {buildingInfo?.company?.name && (
                  <span className="text-gray-400">
                    ({buildingInfo.company.name})
                  </span>
                )}
              </div>
            </div>

            {/* ==================== SECTION: PROJECTONDERDELEN ==================== */}
            <div className="space-y-4">
              <div className="flex items-center justify-between border-b pb-2">
                <div className="flex items-center gap-2">
                  <PencilSimple className="h-4 w-4 text-moss-primary" />
                  <h3 className="font-semibold text-gray-900">
                    Projectonderdelen
                  </h3>
                  <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                    {parts.length} onderdelen
                  </span>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={openAddPartDialog}
                  disabled={isSubmitting || loadingLabels || loadingUnits}
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Voeg onderdeel toe
                </Button>
              </div>

              {parts.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <ListBullets className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>Nog geen projectonderdelen toegevoegd</p>
                  <p className="text-sm">
                    Klik op "Voeg onderdeel toe" om te beginnen
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {parts.map((part) => {
                    const item =
                      part.source === "existing" && part.sourceItemId
                        ? getItemById(part.sourceItemId)
                        : null;
                    const maxAmount =
                      item
                        ? Number(item.estimatedCostExclVat) -
                          Number(item.allocatedBudget || 0)
                        : null;

                    return (
                      <div
                        key={part.id}
                        className="border rounded-lg p-4 space-y-3 bg-white"
                      >
                        {/* Header */}
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            {part.source === "existing" && item ? (
                              <div className="text-xs text-gray-500 mb-1">
                                Van investeringslijn #{item.id}
                              </div>
                            ) : (
                              <div className="text-xs text-blue-600 mb-1 flex items-center gap-1">
                                <CurrencyEur className="h-3 w-3" />
                                Nieuw budget
                              </div>
                            )}
                            <Input
                              value={part.partName}
                              onChange={(e) =>
                                updatePart(part.id, { partName: e.target.value })
                              }
                              placeholder="Naam onderdeel..."
                              disabled={isSubmitting}
                              className="font-medium"
                            />
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removePart(part.id)}
                            disabled={isSubmitting}
                            className="text-gray-400 hover:text-red-500 ml-2"
                          >
                            <Trash className="h-4 w-4" />
                          </Button>
                        </div>

                        {/* Info row */}
                        <div className="flex items-center gap-4 text-sm">
                          {/* Label */}
                          <div className="flex items-center gap-1 text-gray-600">
                            <Tag className="h-3 w-3 text-brick-primary" />
                            {part.source === "existing" && item?.label
                              ? item.label.name
                              : part.labelName || "Geen categorie"}
                          </div>
                          {/* Max budget for existing */}
                          {maxAmount !== null && (
                            <div className="text-gray-500 ml-auto">
                              Max: {formatCurrency(maxAmount)}
                            </div>
                          )}
                        </div>

                        {/* Units (Kostenplaatsen) - multi-select */}
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <Label className="text-xs text-gray-500">Kostenplaatsen:</Label>
                            {buildingUnits.length > 0 && (
                              <div className="flex items-center gap-1">
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    const allUnitIds = buildingUnits
                                      .filter((u) => u.id)
                                      .map((u) => u.id!);
                                    updatePart(part.id, { unitIds: allUnitIds });
                                  }}
                                  disabled={isSubmitting}
                                  className="text-xs h-6 px-2"
                                >
                                  Alles
                                </Button>
                                {part.unitIds.length > 0 && (
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => updatePart(part.id, { unitIds: [] })}
                                    disabled={isSubmitting}
                                    className="text-xs h-6 px-2 text-red-600"
                                  >
                                    Wissen
                                  </Button>
                                )}
                              </div>
                            )}
                          </div>
                          {buildingUnits.length === 0 ? (
                            <div className="text-xs text-gray-400 italic">
                              Geen units gevonden voor dit gebouw
                            </div>
                          ) : (
                            <div className="flex flex-wrap gap-1">
                              {buildingUnits
                                .filter((u) => u.id)
                                .map((unit) => {
                                  const isSelected = part.unitIds.includes(unit.id!);
                                  return (
                                    <button
                                      key={unit.id}
                                      type="button"
                                      onClick={() => {
                                        if (isSelected) {
                                          updatePart(part.id, {
                                            unitIds: part.unitIds.filter((id) => id !== unit.id),
                                          });
                                        } else {
                                          updatePart(part.id, {
                                            unitIds: [...part.unitIds, unit.id!],
                                          });
                                        }
                                      }}
                                      disabled={isSubmitting}
                                      className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded-md border transition-colors ${
                                        isSelected
                                          ? "bg-moss-primary text-white border-moss-primary"
                                          : "bg-gray-50 text-gray-600 border-gray-200 hover:border-moss-primary hover:bg-moss-primary/10"
                                      }`}
                                    >
                                      <Cube className="h-3 w-3" />
                                      {unit.id}
                                    </button>
                                  );
                                })}
                            </div>
                          )}
                          {part.unitIds.length > 0 && (
                            <div className="text-xs text-gray-500">
                              {part.unitIds.length} kostenplaats(en) → elk {(100 / part.unitIds.length).toFixed(1)}% verdeling
                            </div>
                          )}
                        </div>

                        {/* Amount input */}
                        <div className="space-y-2">
                          <div className="flex items-center gap-3">
                            <Label className="text-sm whitespace-nowrap">
                              Budget:
                            </Label>
                            <div className="relative flex-1">
                              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">
                                €
                              </span>
                              <Input
                                type="number"
                                value={part.amount === 0 ? "" : part.amount}
                                onChange={(e) =>
                                  handleAmountChange(part.id, e.target.value)
                                }
                                className="pl-7 text-right"
                                min={0}
                                step={100}
                                disabled={isSubmitting}
                                placeholder="Voer bedrag in..."
                              />
                            </div>
                            {maxAmount !== null && maxAmount > 0 && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() =>
                                  handleAmountChange(part.id, maxAmount.toString())
                                }
                                disabled={isSubmitting}
                                className="text-xs"
                              >
                                Max
                              </Button>
                            )}
                          </div>

                          {/* Overflow handling for existing parts */}
                          {(() => {
                            const overflow = getPartOverflow(part);
                            if (!overflow.hasOverflow) return null;

                            const hasIncreaseOption = part.budgetIncreaseAmount && part.budgetIncreaseAmount > 0;

                            return (
                              <div className="p-3 rounded-lg border border-amber-300 bg-amber-50 space-y-3">
                                <div className="flex items-start gap-2">
                                  <Warning className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
                                  <div className="text-sm text-amber-800">
                                    <span className="font-medium">Extra budget nodig:</span>{" "}
                                    {formatCurrency(overflow.overflowAmount)}
                                  </div>
                                </div>

                                <div className="space-y-2">
                                  <Label className="text-xs text-amber-700 font-medium">
                                    Hoe wil je dit oplossen?
                                  </Label>

                                  {/* Option 1: Increase existing line */}
                                  <button
                                    type="button"
                                    onClick={() =>
                                      updatePart(part.id, {
                                        budgetIncreaseAmount: overflow.overflowAmount,
                                        budgetIncreaseReason: part.budgetIncreaseReason || "",
                                      })
                                    }
                                    disabled={isSubmitting}
                                    className={`w-full text-left p-2 rounded-md border transition-colors ${
                                      hasIncreaseOption
                                        ? "bg-amber-100 border-amber-400"
                                        : "bg-white border-gray-200 hover:border-amber-300"
                                    }`}
                                  >
                                    <div className="flex items-center gap-2">
                                      <ArrowUp className={`h-4 w-4 ${hasIncreaseOption ? "text-amber-600" : "text-gray-400"}`} />
                                      <div>
                                        <div className="text-sm font-medium">Verhoog bestaande investeringslijn</div>
                                        <div className="text-xs text-gray-500">
                                          Budget van #{part.sourceItemId} wordt verhoogd met {formatCurrency(overflow.overflowAmount)}
                                        </div>
                                      </div>
                                    </div>
                                  </button>

                                  {/* Option 2: Split - use remaining and create new (info only) */}
                                  <div className="p-2 rounded-md border border-gray-200 bg-gray-50">
                                    <div className="flex items-center gap-2 text-gray-500">
                                      <PlusCircle className="h-4 w-4" />
                                      <div>
                                        <div className="text-sm">Nieuwe investeringslijn aanmaken</div>
                                        <div className="text-xs">
                                          Gebruik "Voeg onderdeel toe" om een nieuwe lijn aan te maken
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>

                                {/* Budget increase reason (when option 1 selected) */}
                                {hasIncreaseOption && (
                                  <div className="space-y-1 pt-2 border-t border-amber-200">
                                    <Label className="text-xs text-amber-700">
                                      Reden voor budgetverhoging *
                                    </Label>
                                    <Textarea
                                      value={part.budgetIncreaseReason || ""}
                                      onChange={(e) =>
                                        updatePart(part.id, {
                                          budgetIncreaseReason: e.target.value,
                                        })
                                      }
                                      placeholder="Waarom moet het budget verhoogd worden?"
                                      rows={2}
                                      disabled={isSubmitting}
                                      className="text-sm bg-white"
                                    />
                                    <p className="text-xs text-amber-600">
                                      Dit wordt vastgelegd in de audit trail.
                                    </p>
                                  </div>
                                )}

                                {/* Warning if no option selected */}
                                {!hasIncreaseOption && (
                                  <p className="text-xs text-red-600 font-medium">
                                    Selecteer een optie om door te gaan
                                  </p>
                                )}
                              </div>
                            );
                          })()}
                        </div>

                        {/* Supplier selection */}
                        <div className="space-y-2 pt-3 border-t">
                          <Label className="text-xs text-gray-500">Leverancier (optioneel):</Label>
                          <SupplierSearchSelect
                            value={part.supplier || null}
                            onChange={(supplier) => updatePart(part.id, { supplier: supplier || undefined })}
                            linkedSuppliers={[]}
                            placeholder="Zoek en koppel leverancier..."
                          />
                          {part.supplier && (
                            <p className="text-xs text-gray-500">
                              Leverancier wordt na aanmaken aan dit onderdeel gekoppeld.
                            </p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* ==================== TOTALS SUMMARY ==================== */}
            {parts.length > 0 && (
              <div className="rounded-lg bg-moss-primary/5 border border-moss-primary/20 p-4 space-y-2">
                <h4 className="text-sm font-medium text-gray-700">
                  Samenvatting
                </h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  {calculations.totalFromExisting > 0 && (
                    <div>
                      <span className="text-gray-500">
                        Van bestaande lijnen:
                      </span>
                      <span className="ml-2 font-medium">
                        {formatCurrency(calculations.totalFromExisting)}
                      </span>
                    </div>
                  )}
                  {calculations.totalFromNew > 0 && (
                    <div>
                      <span className="text-gray-500">Nieuw budget:</span>
                      <span className="ml-2 font-medium text-blue-600">
                        {formatCurrency(calculations.totalFromNew)}
                      </span>
                    </div>
                  )}
                  <div className="col-span-2 pt-2 border-t">
                    <span className="text-gray-700 font-medium">
                      Budget voor dit project:
                    </span>
                    <span className="ml-2 font-bold text-lg text-moss-primary">
                      {formatCurrency(calculations.total)}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Annuleren
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={
                isSubmitting || !projectTitle.trim() || calculations.total <= 0
              }
              className="bg-moss-primary hover:bg-moss-primaryDark"
            >
              {isSubmitting ? (
                <>
                  <Spinner className="h-4 w-4 mr-2 animate-spin" />
                  Bezig...
                </>
              ) : (
                <>
                  <Rocket className="h-4 w-4 mr-2" />
                  Creëer project ({formatCurrency(calculations.total)})
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ==================== ADD PART DIALOG ==================== */}
      <AlertDialog
        open={addPartDialog.isOpen}
        onOpenChange={(open) => {
          if (!open) {
            setAddPartDialog((prev) => ({ ...prev, isOpen: false }));
          }
        }}
      >
        <AlertDialogContent className="max-w-lg max-h-[85vh] overflow-hidden flex flex-col">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Plus className="h-5 w-5 text-moss-primary" />
              Projectonderdeel toevoegen
            </AlertDialogTitle>
            <AlertDialogDescription>
              {addPartDialog.step === "unit" &&
                "Stap 1: Selecteer kostenplaats(en)"}
              {addPartDialog.step === "source" &&
                "Stap 2: Kies budget of maak nieuw aan"}
              {addPartDialog.step === "distribution" &&
                "Stap 3: Verdeel budget over kostenplaatsen"}
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="py-4 space-y-4 overflow-y-auto flex-1">
            {/* Step 1: Unit selection (multi-select) - FIRST */}
            {addPartDialog.step === "unit" && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label>Kostenplaatsen * <span className="text-xs text-gray-400">(meerdere mogelijk)</span></Label>
                  {!loadingUnits && filteredUnits.length > 0 && (
                    <div className="flex items-center gap-2">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          const allUnitIds = filteredUnits.map((u) => u.id!);
                          setAddPartDialog((prev) => ({
                            ...prev,
                            unitIds: allUnitIds,
                          }));
                        }}
                        className="text-xs h-7 px-2"
                      >
                        Selecteer alles
                      </Button>
                      {addPartDialog.unitIds.length > 0 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setAddPartDialog((prev) => ({
                              ...prev,
                              unitIds: [],
                            }));
                          }}
                          className="text-xs h-7 px-2 text-red-600"
                        >
                          Deselecteer alles
                        </Button>
                      )}
                    </div>
                  )}
                </div>

                {/* Search input */}
                <div className="relative">
                  <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    type="text"
                    value={unitSearch}
                    onChange={(e) => setUnitSearch(e.target.value)}
                    placeholder="Zoek kostenplaats..."
                    className="pl-9"
                    autoFocus
                  />
                </div>

                {loadingUnits ? (
                  <div className="flex items-center gap-2 text-gray-500">
                    <Spinner className="h-4 w-4 animate-spin" />
                    Laden...
                  </div>
                ) : filteredUnits.length === 0 ? (
                  <div className="text-center py-4 text-gray-500">
                    <Cube className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    {unitSearch ? (
                      <p>Geen kostenplaatsen gevonden voor "{unitSearch}"</p>
                    ) : (
                      <>
                        <p>Geen kostenplaatsen gevonden</p>
                        <p className="text-sm">Controleer of het gebouw units heeft</p>
                      </>
                    )}
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                      {filteredUnits.map((unit) => {
                        const isSelected = addPartDialog.unitIds.includes(unit.id!);
                        return (
                          <Button
                            key={unit.id}
                            type="button"
                            variant={isSelected ? "default" : "outline"}
                            className={`justify-start ${isSelected ? "bg-moss-primary" : ""}`}
                            onClick={() => {
                              setAddPartDialog((prev) => ({
                                ...prev,
                                unitIds: isSelected
                                  ? prev.unitIds.filter((id) => id !== unit.id)
                                  : [...prev.unitIds, unit.id!],
                              }));
                            }}
                          >
                            <Cube className="h-4 w-4 mr-2" />
                            {unit.id}
                          </Button>
                        );
                      })}
                    </div>
                    {addPartDialog.unitIds.length > 0 && (
                      <div className="pt-2 border-t space-y-2">
                        <div className="text-xs text-gray-500">
                          {addPartDialog.unitIds.length} kostenplaats(en) geselecteerd → elk {(100 / addPartDialog.unitIds.length).toFixed(1)}% verdeling
                        </div>
                        <Button
                          type="button"
                          onClick={handleUnitsConfirm}
                          className="w-full bg-moss-primary hover:bg-moss-primaryDark"
                        >
                          Bekijk beschikbare budgetten
                        </Button>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Step 2: Source selection - Show available budgets with label filter */}
            {addPartDialog.step === "source" && (
              <div className="space-y-4">
                {/* Selection summary */}
                <div className="flex items-center gap-2 text-sm bg-gray-50 p-2 rounded">
                  <Cube className="h-4 w-4 text-moss-primary" />
                  <span className="font-medium">
                    {addPartDialog.unitIds.length === 1
                      ? addPartDialog.unitIds[0]
                      : `${addPartDialog.unitIds.length} kostenplaatsen`}
                  </span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      setAddPartDialog((prev) => ({ ...prev, step: "unit" }))
                    }
                    className="ml-auto text-xs"
                  >
                    Wijzig
                  </Button>
                </div>

                {/* Label filter */}
                <div className="space-y-2">
                  <Label className="text-xs text-gray-500">Filter op categorie:</Label>
                  <div className="flex flex-wrap gap-1">
                    <button
                      type="button"
                      onClick={() => handleLabelFilterChange(null)}
                      className={`px-2 py-1 text-xs rounded-full border transition-colors ${
                        addPartDialog.labelFilter === null
                          ? "bg-moss-primary text-white border-moss-primary"
                          : "bg-gray-50 text-gray-600 border-gray-200 hover:border-moss-primary"
                      }`}
                    >
                      Alle
                    </button>
                    {labels.map((label) => (
                      <button
                        key={label.id}
                        type="button"
                        onClick={() => handleLabelFilterChange(label.id)}
                        className={`px-2 py-1 text-xs rounded-full border transition-colors ${
                          addPartDialog.labelFilter === label.id
                            ? "bg-brick-primary text-white border-brick-primary"
                            : "bg-gray-50 text-gray-600 border-gray-200 hover:border-brick-primary"
                        }`}
                      >
                        {label.name}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Search budgets */}
                <div className="relative">
                  <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    type="text"
                    value={budgetSearch}
                    onChange={(e) => setBudgetSearch(e.target.value)}
                    placeholder="Zoek in budgetten..."
                    className="pl-9"
                  />
                </div>

                {/* Available budgets */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Beschikbare budgetten</Label>
                    <span className="text-xs text-gray-500">
                      {filteredMatchingItems.length} gevonden
                    </span>
                  </div>

                  {filteredMatchingItems.length > 0 && (
                    <TooltipProvider delayDuration={300}>
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {filteredMatchingItems.map((item) => {
                          const remaining =
                            Number(item.estimatedCostExclVat) -
                            Number(item.allocatedBudget || 0);
                          const isSelected =
                            addPartDialog.selectedItemId === item.id &&
                            !addPartDialog.isNewBudget;
                          const truncatedDesc = truncateToSentences(item.description, 2);
                          const creationReason = getCreationReason(item);
                          const hasMoreContent =
                            (item.description && item.description.length > truncatedDesc.length) ||
                            creationReason;

                          return (
                            <Tooltip key={item.id}>
                              <TooltipTrigger asChild>
                                <button
                                  type="button"
                                  className={`w-full text-left rounded-md border p-3 transition-colors ${
                                    isSelected
                                      ? "bg-moss-primary text-white border-moss-primary"
                                      : "bg-white border-gray-200 hover:border-moss-primary hover:bg-moss-primary/5"
                                  }`}
                                  onClick={() => handleSourceSelect(item.id, false)}
                                >
                                  <div className="space-y-1">
                                    <div className="flex items-start justify-between gap-2">
                                      <span className="font-medium text-sm">{item.title}</span>
                                      <span className={`text-xs shrink-0 ${isSelected ? "opacity-80" : "text-moss-primary font-medium"}`}>
                                        {formatCurrency(remaining)}
                                      </span>
                                    </div>
                                    <div className={`text-xs ${isSelected ? "opacity-70" : "text-gray-500"}`}>
                                      #{item.id} • Beschikbaar
                                    </div>
                                    {truncatedDesc && (
                                      <p className={`text-xs leading-relaxed ${isSelected ? "opacity-70" : "text-gray-600"}`}>
                                        {truncatedDesc}
                                      </p>
                                    )}
                                    {creationReason && (
                                      <p className={`text-xs italic ${isSelected ? "opacity-60" : "text-amber-600"}`}>
                                        Reden: {truncateToSentences(creationReason, 1)}
                                      </p>
                                    )}
                                  </div>
                                </button>
                              </TooltipTrigger>
                              {hasMoreContent && (
                                <TooltipContent
                                  side="right"
                                  align="start"
                                  className="max-w-xs max-h-48 overflow-y-auto bg-white text-gray-900 border shadow-lg p-3 space-y-2"
                                >
                                  <div className="font-medium text-sm border-b pb-2">{item.title}</div>
                                  {item.description && (
                                    <div className="space-y-1">
                                      <span className="text-xs font-medium text-gray-500">Beschrijving:</span>
                                      <p className="text-xs text-gray-700 leading-relaxed">{item.description}</p>
                                    </div>
                                  )}
                                  {creationReason && (
                                    <div className="space-y-1 pt-1 border-t">
                                      <span className="text-xs font-medium text-amber-600">Reden aanmaak:</span>
                                      <p className="text-xs text-amber-700 leading-relaxed">{creationReason}</p>
                                    </div>
                                  )}
                                </TooltipContent>
                              )}
                            </Tooltip>
                          );
                        })}
                      </div>
                    </TooltipProvider>
                  )}

                  {addPartDialog.matchingItems.length === 0 && (
                    <div className="text-sm text-gray-500 italic py-2">
                      Geen bestaande investeringslijnen gevonden met deze
                      categorie en kostenplaats.
                    </div>
                  )}

                  {/* New budget option */}
                  <div className="pt-2 border-t">
                    <Button
                      type="button"
                      variant={addPartDialog.isNewBudget ? "default" : "outline"}
                      className={`w-full justify-start h-auto py-2 ${addPartDialog.isNewBudget ? "bg-blue-600" : ""}`}
                      onClick={() => handleSourceSelect(null, true)}
                    >
                      <CurrencyEur className="h-4 w-4 mr-2" />
                      <div className="text-left">
                        <div className="font-medium">Nieuw budget aanmaken</div>
                        <div className="text-xs opacity-80">
                          Maakt een nieuwe investeringslijn aan
                        </div>
                      </div>
                    </Button>
                  </div>
                </div>

                {/* Name and amount fields (shown after selection) */}
                {(addPartDialog.selectedItemId || addPartDialog.isNewBudget) && (
                  <div className="space-y-3 pt-4 border-t">
                    {/* Label selector for new budget (only if not using label filter) */}
                    {addPartDialog.isNewBudget && (
                      <div className="space-y-2">
                        <Label className="text-sm">Categorie voor nieuwe investeringslijn *</Label>
                        <div className="flex flex-wrap gap-1">
                          {labels.map((label) => (
                            <button
                              key={label.id}
                              type="button"
                              onClick={() =>
                                setAddPartDialog((prev) => ({
                                  ...prev,
                                  newBudgetLabelId: label.id,
                                  // Update part name if it was auto-generated
                                  partName: prev.partName.startsWith("Nieuw") || prev.partName.includes(" - ")
                                    ? `${label.name} - ${prev.unitIds.length === 1 ? prev.unitIds[0] : `${prev.unitIds.length} units`}`
                                    : prev.partName,
                                }))
                              }
                              className={`px-2 py-1 text-xs rounded-full border transition-colors ${
                                addPartDialog.newBudgetLabelId === label.id
                                  ? "bg-brick-primary text-white border-brick-primary"
                                  : "bg-gray-50 text-gray-600 border-gray-200 hover:border-brick-primary"
                              }`}
                            >
                              <Tag className="h-3 w-3 inline mr-1" />
                              {label.name}
                            </button>
                          ))}
                        </div>
                        {!addPartDialog.newBudgetLabelId && (
                          <p className="text-xs text-amber-600">
                            Selecteer een categorie voor de nieuwe investeringslijn
                          </p>
                        )}
                      </div>
                    )}

                    <div className="space-y-1">
                      <Label className="text-sm">Naam onderdeel *</Label>
                      <Input
                        value={addPartDialog.partName}
                        onChange={(e) =>
                          setAddPartDialog((prev) => ({
                            ...prev,
                            partName: e.target.value,
                          }))
                        }
                        placeholder="Naam voor dit onderdeel..."
                      />
                    </div>

                    <div className="space-y-1">
                      <Label className="text-sm">Budget *</Label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">
                          €
                        </span>
                        <Input
                          type="number"
                          value={addPartDialog.amount === 0 ? "" : addPartDialog.amount}
                          onChange={(e) => {
                            const value = e.target.value;
                            const numValue = value === "" ? 0 : parseFloat(value);
                            setAddPartDialog((prev) => ({
                              ...prev,
                              amount: isNaN(numValue) ? 0 : numValue,
                              // Reset overflow option when amount changes
                              overflowOption: null,
                              overflowReason: "",
                            }));
                          }}
                          className="pl-7"
                          min={0}
                          step={100}
                          placeholder="Voer bedrag in..."
                        />
                      </div>
                      {addPartDialog.selectedItemId && !addPartDialog.isNewBudget && (() => {
                        const item = getItemById(addPartDialog.selectedItemId);
                        if (!item) return null;
                        const remaining = Number(item.estimatedCostExclVat) - Number(item.allocatedBudget || 0);
                        const overflowAmount = addPartDialog.amount - remaining;
                        const hasOverflow = overflowAmount > 0;

                        return (
                          <>
                            <div className={`text-xs ${remaining === 0 ? "text-amber-600" : "text-gray-500"}`}>
                              {remaining === 0 ? (
                                <>Budget volledig toegewezen - voer het gewenste bedrag in</>
                              ) : (
                                <>Max beschikbaar: {formatCurrency(remaining)}</>
                              )}
                            </div>

                            {/* Overflow warning and options */}
                            {hasOverflow && (
                              <div className="mt-3 p-3 rounded-lg border border-amber-300 bg-amber-50 space-y-3">
                                <div className="flex items-start gap-2">
                                  <Warning className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
                                  <div className="text-sm text-amber-800">
                                    <span className="font-medium">Extra budget nodig:</span>{" "}
                                    {formatCurrency(overflowAmount)}
                                  </div>
                                </div>

                                <div className="space-y-2">
                                  <Label className="text-xs text-amber-700 font-medium">
                                    Hoe wil je dit oplossen?
                                  </Label>

                                  {/* Option 1: Increase existing line */}
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setAddPartDialog((prev) => ({
                                        ...prev,
                                        overflowOption: "increase",
                                        newBudgetLabelId: null,
                                      }))
                                    }
                                    className={`w-full text-left p-2 rounded-md border transition-colors ${
                                      addPartDialog.overflowOption === "increase"
                                        ? "bg-amber-100 border-amber-400"
                                        : "bg-white border-gray-200 hover:border-amber-300"
                                    }`}
                                  >
                                    <div className="flex items-center gap-2">
                                      <ArrowUp className={`h-4 w-4 ${addPartDialog.overflowOption === "increase" ? "text-amber-600" : "text-gray-400"}`} />
                                      <div>
                                        <div className="text-sm font-medium">Verhoog bestaande investeringslijn</div>
                                        <div className="text-xs text-gray-500">
                                          Budget van #{item.id} wordt verhoogd met {formatCurrency(overflowAmount)}
                                        </div>
                                      </div>
                                    </div>
                                  </button>

                                  {/* Option 2: Create new line */}
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setAddPartDialog((prev) => ({
                                        ...prev,
                                        overflowOption: "new_line",
                                        overflowReason: "",
                                        newBudgetLabelId: item.label?.id || null,
                                      }))
                                    }
                                    className={`w-full text-left p-2 rounded-md border transition-colors ${
                                      addPartDialog.overflowOption === "new_line"
                                        ? "bg-blue-100 border-blue-400"
                                        : "bg-white border-gray-200 hover:border-blue-300"
                                    }`}
                                  >
                                    <div className="flex items-center gap-2">
                                      <PlusCircle className={`h-4 w-4 ${addPartDialog.overflowOption === "new_line" ? "text-blue-600" : "text-gray-400"}`} />
                                      <div>
                                        <div className="text-sm font-medium">Maak nieuwe investeringslijn</div>
                                        <div className="text-xs text-gray-500">
                                          Nieuwe lijn van {formatCurrency(overflowAmount)} voor het extra budget
                                        </div>
                                      </div>
                                    </div>
                                  </button>
                                </div>

                                {/* Budget increase reason (for option 1) */}
                                {addPartDialog.overflowOption === "increase" && (
                                  <div className="space-y-1 pt-2 border-t border-amber-200">
                                    <Label className="text-xs text-amber-700">
                                      Reden voor budgetverhoging *
                                    </Label>
                                    <Textarea
                                      value={addPartDialog.overflowReason}
                                      onChange={(e) =>
                                        setAddPartDialog((prev) => ({
                                          ...prev,
                                          overflowReason: e.target.value,
                                        }))
                                      }
                                      placeholder="Waarom moet het budget verhoogd worden?"
                                      rows={2}
                                      className="text-sm bg-white"
                                    />
                                    <p className="text-xs text-amber-600">
                                      Dit wordt vastgelegd in de audit trail.
                                    </p>
                                  </div>
                                )}

                                {/* Label selection for new line (option 2) */}
                                {addPartDialog.overflowOption === "new_line" && (
                                  <div className="space-y-2 pt-2 border-t border-blue-200">
                                    <Label className="text-xs text-blue-700">
                                      Categorie voor nieuwe investeringslijn *
                                    </Label>
                                    <div className="flex flex-wrap gap-1">
                                      {labels.map((label) => (
                                        <button
                                          key={label.id}
                                          type="button"
                                          onClick={() =>
                                            setAddPartDialog((prev) => ({
                                              ...prev,
                                              newBudgetLabelId: label.id,
                                            }))
                                          }
                                          className={`px-2 py-1 text-xs rounded-full border transition-colors ${
                                            addPartDialog.newBudgetLabelId === label.id
                                              ? "bg-blue-600 text-white border-blue-600"
                                              : "bg-white text-gray-600 border-gray-200 hover:border-blue-400"
                                          }`}
                                        >
                                          {label.name}
                                        </button>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Step 3: Unit Distribution */}
            {addPartDialog.step === "distribution" && (
              <div className="space-y-4">
                {/* Back button and summary */}
                <div className="flex items-center justify-between">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      setAddPartDialog((prev) => ({ ...prev, step: "source" }))
                    }
                    className="text-xs"
                  >
                    <ArrowLeft className="mr-1 h-4 w-4" />
                    Terug
                  </Button>
                  <div className="text-right">
                    <p className="text-sm font-medium text-moss-primary">
                      {formatCurrency(addPartDialog.amount)}
                    </p>
                    <p className="text-xs text-neutral-500">Te verdelen</p>
                  </div>
                </div>

                {/* Distribution header */}
                <div className="rounded-md bg-neutral-50 p-3">
                  <div className="flex items-center gap-2 text-sm font-medium text-neutral-700">
                    <Percent className="h-4 w-4 text-moss-primary" />
                    Verdeling per kostenplaats
                  </div>
                  <p className="mt-1 text-xs text-neutral-500">
                    Verdeel het budget over de geselecteerde kostenplaatsen.
                    De percentages moeten optellen tot 100%.
                  </p>
                </div>

                {/* Distribution inputs */}
                <div className="space-y-3">
                  {addPartDialog.unitIds.map((unitId) => {
                    const unit = getUnitDetails(unitId);
                    const percentage = addPartDialog.unitDistribution[unitId] || 0;
                    const calculatedAmount = (addPartDialog.amount * percentage) / 100;
                    const surface = unit?.netSurface || unit?.grossSurface || 0;
                    const eurPerM2 = surface > 0 ? calculatedAmount / surface : 0;

                    return (
                      <div
                        key={unitId}
                        className="rounded-md border border-neutral-200 bg-white p-3"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <Cube className="h-4 w-4 text-moss-primary" />
                              <span className="font-medium text-neutral-700">
                                {unitId}
                              </span>
                            </div>
                            {surface > 0 && (
                              <div className="mt-1 flex items-center gap-1 text-xs text-neutral-500">
                                <Ruler className="h-3 w-3" />
                                {surface.toFixed(0)} m²
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="relative w-20">
                              <Input
                                type="number"
                                value={percentage || ""}
                                onChange={(e) => {
                                  const value = e.target.value === "" ? 0 : parseFloat(e.target.value);
                                  handleDistributionChange(unitId, isNaN(value) ? 0 : value);
                                }}
                                className="pr-6 text-right"
                                min={0}
                                max={100}
                                step={0.5}
                              />
                              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-sm text-neutral-400">
                                %
                              </span>
                            </div>
                          </div>
                        </div>
                        {/* Calculated amount and €/m² */}
                        <div className="mt-2 flex items-center justify-between border-t border-neutral-100 pt-2">
                          <span className="text-sm text-moss-primary font-medium">
                            {formatCurrency(calculatedAmount)}
                          </span>
                          {surface > 0 && calculatedAmount > 0 && (
                            <span className="text-xs text-neutral-500">
                              {formatCurrency(eurPerM2)}/m²
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Total percentage indicator */}
                <div
                  className={`flex items-center justify-between rounded-md p-3 ${
                    isDistributionValid
                      ? "bg-green-50 border border-green-200"
                      : "bg-amber-50 border border-amber-200"
                  }`}
                >
                  <span className="text-sm font-medium">Totaal:</span>
                  <span
                    className={`text-sm font-bold ${
                      isDistributionValid ? "text-green-700" : "text-amber-700"
                    }`}
                  >
                    {totalDistributionPercentage.toFixed(1)}%
                    {!isDistributionValid && (
                      <span className="ml-2 font-normal text-xs">
                        (moet 100% zijn)
                      </span>
                    )}
                  </span>
                </div>

                {/* Quick actions */}
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const equalPct = 100 / addPartDialog.unitIds.length;
                      const distribution: UnitDistribution = {};
                      addPartDialog.unitIds.forEach((id) => {
                        distribution[id] = Math.round(equalPct * 100) / 100;
                      });
                      setAddPartDialog((prev) => ({
                        ...prev,
                        unitDistribution: distribution,
                      }));
                    }}
                    className="text-xs"
                  >
                    Gelijke verdeling
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      // Distribute by surface area
                      const totalSurface = addPartDialog.unitIds.reduce((sum, id) => {
                        const unit = getUnitDetails(id);
                        return sum + (unit?.netSurface || unit?.grossSurface || 0);
                      }, 0);

                      if (totalSurface === 0) {
                        toast.error("Geen oppervlakte data beschikbaar");
                        return;
                      }

                      const distribution: UnitDistribution = {};
                      addPartDialog.unitIds.forEach((id) => {
                        const unit = getUnitDetails(id);
                        const surface = unit?.netSurface || unit?.grossSurface || 0;
                        distribution[id] = Math.round((surface / totalSurface) * 10000) / 100;
                      });
                      setAddPartDialog((prev) => ({
                        ...prev,
                        unitDistribution: distribution,
                      }));
                    }}
                    className="text-xs"
                  >
                    <Ruler className="mr-1 h-3 w-3" />
                    Verdelen op m²
                  </Button>
                </div>
              </div>
            )}
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel>Annuleren</AlertDialogCancel>
            {/* Step 2: Go to distribution if multiple units */}
            {addPartDialog.step === "source" &&
            (addPartDialog.selectedItemId || addPartDialog.isNewBudget) &&
            addPartDialog.unitIds.length > 1 ? (
              <AlertDialogAction
                onClick={handleProceedToDistribution}
                disabled={(() => {
                  // Basic validation
                  if (!addPartDialog.partName || addPartDialog.amount <= 0) return true;

                  // New budget validation
                  if (addPartDialog.isNewBudget && !addPartDialog.newBudgetLabelId) return true;

                  // Overflow validation for existing item
                  if (addPartDialog.selectedItemId && !addPartDialog.isNewBudget) {
                    const item = getItemById(addPartDialog.selectedItemId);
                    if (item) {
                      const remaining = Number(item.estimatedCostExclVat) - Number(item.allocatedBudget || 0);
                      const hasOverflow = addPartDialog.amount > remaining;

                      if (hasOverflow) {
                        if (!addPartDialog.overflowOption) return true;
                        if (addPartDialog.overflowOption === "increase" &&
                            (!addPartDialog.overflowReason.trim() || addPartDialog.overflowReason.trim().length < 5)) return true;
                        if (addPartDialog.overflowOption === "new_line" && !addPartDialog.newBudgetLabelId) return true;
                      }
                    }
                  }

                  return false;
                })()}
                className="bg-moss-primary hover:bg-moss-primaryDark"
              >
                Verdeling instellen
              </AlertDialogAction>
            ) : addPartDialog.step === "source" &&
              (addPartDialog.selectedItemId || addPartDialog.isNewBudget) &&
              addPartDialog.unitIds.length === 1 ? (
              <AlertDialogAction
                onClick={confirmAddPart}
                disabled={(() => {
                  // Basic validation
                  if (!addPartDialog.partName || addPartDialog.amount <= 0) return true;

                  // New budget validation
                  if (addPartDialog.isNewBudget && !addPartDialog.newBudgetLabelId) return true;

                  // Overflow validation for existing item
                  if (addPartDialog.selectedItemId && !addPartDialog.isNewBudget) {
                    const item = getItemById(addPartDialog.selectedItemId);
                    if (item) {
                      const remaining = Number(item.estimatedCostExclVat) - Number(item.allocatedBudget || 0);
                      const hasOverflow = addPartDialog.amount > remaining;

                      if (hasOverflow) {
                        if (!addPartDialog.overflowOption) return true;
                        if (addPartDialog.overflowOption === "increase" &&
                            (!addPartDialog.overflowReason.trim() || addPartDialog.overflowReason.trim().length < 5)) return true;
                        if (addPartDialog.overflowOption === "new_line" && !addPartDialog.newBudgetLabelId) return true;
                      }
                    }
                  }

                  return false;
                })()}
                className="bg-moss-primary hover:bg-moss-primaryDark"
              >
                Toevoegen
              </AlertDialogAction>
            ) : addPartDialog.step === "distribution" ? (
              <AlertDialogAction
                onClick={confirmAddPart}
                disabled={!isDistributionValid}
                className="bg-moss-primary hover:bg-moss-primaryDark"
              >
                Toevoegen
              </AlertDialogAction>
            ) : (
              <Button disabled className="opacity-50">
                Selecteer eerst...
              </Button>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
